import { useDispatch } from "react-redux";
import {
  setAllCampaigns,
  setCostWeightchartData,
  setLpLoader,
  setPerformanceByKeywords,
  setRelevantAccountsBasedOnSite,
  setSelectedLPAccount,
  setSelectedLPCampaign,
  setSelectedLPSharedUid,
  setSiteDimensions,
} from "../../../features/landingPageExperienceSlice";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/en-gb"; // Import the locale for British English
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../features/userSlice";
import { LPExperienceEndPointLogic } from "../../../logic/PaidTrafficManagement/LandingPageExperience/landingPageExperienceEndPointLogic";
import { useEffect, useState } from "react";

export const useLPExperience = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [isActiveCampaigns, setIsActiveCampaigns] = useState(true);

  const lp_experience_campaign_query_id = "5728744266268672"; // for campaigns
  const cost_weight_chart_query_id = "5696446112202752"; // for charts
  const performance_by_keyword_query_id = "5631003024424960"; // for keyword performance
  const type = "site";
  const {
    selectedLPSharedUid,
    selectedLPCampaign,
    allAdvertisingAccounts,
    relevantAccountsBasedOnSite,
    selectedLPAccount,
    selectedLPDateRange,
    //allCampaigns,
    siteDimensions,
  } = useSelector((state: any) => state.landingPageExperience);

  // Check if all required data is available(Get Campaigns)
  const ready =
    !!oauthTokenHeader &&
    !!selectedLPSharedUid &&
    !!selectedLPAccount &&
    siteDimensions?.length > 0 &&
    relevantAccountsBasedOnSite?.length > 0;

  // Fetch LP experience data from API
  const fetchedResults = async () => {
    let siteDimensionsArrayFromURL: {
      dimension_shared_uid: string;
      dimension_name: string;
    }[] = [];

    try {
      dispatch(setLpLoader(true)); // Assuming you have a loading state in Redux

      const siteFromURL = searchParams.get("selectedSite");
      const accountFromURL = searchParams.get("account");
      const dateRangeFromURL = searchParams.get("lp_date_range");
      const campaignFromURL = searchParams.get("campaign");
      const dimensionFromURL = searchParams.get("site_dimensions") ?? "[]";
      siteDimensionsArrayFromURL = JSON.parse(dimensionFromURL);

      let urlStartDate = null;
      let urlEndDate = null;

      if (dateRangeFromURL) {
        const [start, end] = dateRangeFromURL.split("&"); // split by actual &
        urlStartDate = dayjs(start).format("YYYY-MM-DD");
        urlEndDate = dayjs(end).format("YYYY-MM-DD");
      }
      // Prepare shared payload
      const startDate = selectedLPDateRange?.[0]?.format("YYYY-MM-DD")
        ? selectedLPDateRange?.[0]?.format("YYYY-MM-DD")
        : urlStartDate;
      const endDate = selectedLPDateRange?.[1]?.format("YYYY-MM-DD")
        ? selectedLPDateRange?.[1]?.format("YYYY-MM-DD")
        : urlEndDate;

      const campaign_id = selectedLPCampaign
        ? selectedLPCampaign
        : campaignFromURL;

      const commonPayload = {
        oauthTokenHeader,
        startDate: startDate,
        endDate: endDate,
        websiteSharedUid: selectedLPSharedUid
          ? selectedLPSharedUid
          : siteFromURL,
        platformAccountId: selectedLPAccount
          ? selectedLPAccount
          : accountFromURL,
        kwInternalId: 1,
        campaignId: campaign_id || 1,
      };

      // Run API calls in parallel
      await Promise.all([
        LPExperienceEndPointLogic.getLPCostWeightedScoreChart({
          ...commonPayload,
          queryId: cost_weight_chart_query_id,
          type: type,
          dimensions:
            siteDimensions?.length > 0
              ? siteDimensions
              : siteDimensionsArrayFromURL, //"bbb_uk"
        }),
        LPExperienceEndPointLogic.getLPPerformanceByKeyword({
          ...commonPayload,
          queryId: performance_by_keyword_query_id,
        }),
      ]);
    } catch (error) {
      console.error("Error fetching LP data:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch Landing Page Experience data",
          type: "error",
        })
      );
    } finally {
      // Stop loading
      dispatch(setLpLoader(false));
    }
  };

  // set data on URL
  const updateURLParamsOfLP = (forceClicked?: boolean) => {
    const updatedParams = new URLSearchParams(searchParams);

    if (selectedLPSharedUid) {
      updatedParams.set("selectedSite", selectedLPSharedUid);
    } else {
      updatedParams.delete("selectedSite");
    }

    if (selectedLPAccount) {
      updatedParams.set("account", selectedLPAccount);
      // Or use another property instead of `id` based on your object shape
    } else {
      updatedParams.delete("account");
    }

    if (
      selectedLPDateRange &&
      selectedLPDateRange[0] &&
      selectedLPDateRange[1]
    ) {
      updatedParams.set(
        "lp_date_range",
        `${selectedLPDateRange[0].format(
          "YYYY-MM-DD"
        )}&${selectedLPDateRange[1].format("YYYY-MM-DD")}`
      );
    } else {
      updatedParams.delete("lp_date_range");
    }

    if (selectedLPCampaign) {
      updatedParams.set("campaign", selectedLPCampaign);
    } else {
      updatedParams.delete("campaign");
    }

    updatedParams.set("is_active_campaigns", String(isActiveCampaigns));

    if (siteDimensions && siteDimensions.length > 0) {
      updatedParams.set("site_dimensions", JSON.stringify(siteDimensions));
    } else {
      updatedParams.delete("site_dimensions");
    }

    // is_clicked — preserve from URL OR Redux, allow override on first click
    const isClickedFromUrl = searchParams.get("is_clicked") === "true";
    const clicked = forceClicked ?? isClickedFromUrl;
    if (clicked) {
      updatedParams.set("is_clicked", "true");
    } else {
      updatedParams.delete("is_clicked");
    }

    setSearchParams(updatedParams, { replace: true });
  };

  // Clear all parameters
  const clearParams = () => {
    setSearchParams({}); // clear all URL's query parameters
    dispatch(setSelectedLPSharedUid(""));
    dispatch(setRelevantAccountsBasedOnSite([]));
    dispatch(setSelectedLPAccount(""));
    dispatch(setAllCampaigns([]));
    dispatch(setSelectedLPCampaign(""));
    dispatch(setCostWeightchartData([]));
    dispatch(setPerformanceByKeywords([]));
    dispatch(setSiteDimensions([]));
  };

  //-- Get All Campaigns based on selected website
  useEffect(() => {
    if (!ready) return;
    const dateRangeFromURL = searchParams.get("lp_date_range");
    let urlStartDate = null;
    let urlEndDate = null;
    let siteDimensionsArrayFromURL: {
      dimension_shared_uid: string;
      dimension_name: string;
    }[] = [];

    if (dateRangeFromURL) {
      const [start, end] = dateRangeFromURL.split("&"); // split by actual &
      urlStartDate = dayjs(start).format("YYYY-MM-DD");
      urlEndDate = dayjs(end).format("YYYY-MM-DD");
    }
    // Prepare shared payload
    const startDate = selectedLPDateRange?.[0]?.format("YYYY-MM-DD")
      ? selectedLPDateRange?.[0]?.format("YYYY-MM-DD")
      : urlStartDate;
    const endDate = selectedLPDateRange?.[1]?.format("YYYY-MM-DD")
      ? selectedLPDateRange?.[1]?.format("YYYY-MM-DD")
      : urlEndDate;

    const dimensionFromURL = searchParams.get("site_dimensions") ?? "[]";
    siteDimensionsArrayFromURL = JSON.parse(dimensionFromURL);

    const matchedAccounts =
      relevantAccountsBasedOnSite?.length > 0 &&
      relevantAccountsBasedOnSite.find(
        (account: any) => account.account_id === selectedLPAccount
      );

    const getCampaigns = async () => {
      dispatch(setSelectedLPCampaign("")); // reset campaign as depend on account
      try {
        setLoadingCampaigns(true);
        await LPExperienceEndPointLogic.getAllCampaigns({
          oauthTokenHeader,
          queryId: lp_experience_campaign_query_id, // Replace with actual query ID
          startDate: startDate,
          endDate: endDate,
          traffic_source: matchedAccounts?.traffic_source, // Adjust as needed
          type: type, // Adjust as needed
          websiteSharedUid: selectedLPSharedUid,
          account_shared_uid: 1,
          campaign_status: isActiveCampaigns ? "active" : "all", // Adjust as needed
          dimensions:
            siteDimensions?.length > 0
              ? siteDimensions
              : siteDimensionsArrayFromURL,
        });
      } catch (err) {
        console.error("Error fetching campaigns", err);
      } finally {
        setLoadingCampaigns(false); // stop loader
      }
    };

    getCampaigns();
    //eslint-disable-next-line
  }, [ready, selectedLPSharedUid, selectedLPAccount, isActiveCampaigns]);

  //-- By default Fetch all advertisement accounts if not already fetched
  useEffect(() => {
    if (allAdvertisingAccounts?.length > 0) return;
    LPExperienceEndPointLogic.getAllAdvertisementAccounts(oauthTokenHeader);

    //eslint-disable-next-line
  }, [oauthTokenHeader]);

  return {
    updateURLParamsOfLP,
    fetchedResults,
    clearParams,
    loadingCampaigns,
    isActiveCampaigns,
    setIsActiveCampaigns,
  };
};
